// /payments/new — record one payment that gets distributed across multiple
// open invoices for a single client. Companion to the per-reservation
// AddPayment modal (which keeps working for the common single-res case).
//
// Flow:
//   A. Pick client (customer or company) via ClientSearch
//   B. Fill payment header — monto / fecha / cuenta / forma de pago
//   C. See client's open invoices; type how much to apply per invoice (defaults to $0)
//   D. Submit → INSERT transactions row + invoice_payment_allocations rows;
//      back-update affected reservations' paid/balance/fully_paid via shared helper.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppWrapper from "../AppWrapper";
import {
  Page,
  Card,
  BlockStack,
  InlineGrid,
  Text,
  TextField,
  Select,
  Button,
  Banner,
  IndexTable,
  Spinner,
} from "@shopify/polaris";
import moment from "moment";
import supabase from "../../supabase-client";
import { UserAuth } from "../../context/AuthContext";
import { formatPrice, formatDate } from "../../utility/funcs";
import ClientSearch from "../CustomComponents/ClientSearch/ClientSearch";
import {
  fetchClientOpenInvoices,
  applyAllocations,
} from "../../utility/payments";

// Same payment-method → account routing AddPayment uses, kept verbatim so the
// two flows produce comparable transactions.
const PAYMENT_METHODS = [
  { name: "Efectivo", accounts: [1] },
  { name: "Tarjeta de Crédito", accounts: [4, 5] },
  { name: "Tarjeta de Débito", accounts: [4, 5] },
  { name: "Transferencia", accounts: [2, 3] },
  { name: "Depósito en Efectivo", accounts: [2, 3] },
];

function CreatePayment() {
  const navigate = useNavigate();
  const { session } = UserAuth();

  // ---------- Client ----------
  const [clientSearchOpen, setClientSearchOpen] = useState(false);
  const [client, setClient] = useState(null); // { client_type, id, name, detail }

  // ---------- Open invoices for the picked client ----------
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [allocations, setAllocations] = useState({}); // { [invoice.id]: amount }

  // ---------- Payment header ----------
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [method, setMethod] = useState("Efectivo");
  const [cardLast4, setCardLast4] = useState("");
  const [account, setAccount] = useState(1);
  const [accounts, setAccounts] = useState([]);

  // ---------- Submit state ----------
  const [saving, setSaving] = useState(false);
  // Synchronous re-entry guard against double-clicks on Registrar Pago.
  const savingRef = useRef(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Load all transaction accounts once.
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("transaction_accounts")
        .select("id, name")
        // Clip and Fintoc feed themselves; see transaction_accounts.manual_entry.
        .eq("manual_entry", true)
        .order("order", { ascending: true });
      setAccounts(data || []);
    })();
  }, []);

  // Pull this client's open invoices when they're picked / cleared.
  useEffect(() => {
    if (!client) {
      setInvoices([]);
      setAllocations({});
      return;
    }
    (async () => {
      setLoadingInvoices(true);
      const open = await fetchClientOpenInvoices({
        clientType: client.client_type,
        clientId: client.id,
      });
      setInvoices(open);
      // Default every allocation to 0 per user's preference.
      setAllocations(Object.fromEntries(open.map((i) => [i.id, 0])));
      setLoadingInvoices(false);
    })();
  }, [client]);

  // Method change → snap to the first valid account for that method.
  const handleMethodChange = (val) => {
    setMethod(val);
    const m = PAYMENT_METHODS.find((p) => p.name === val);
    if (m) setAccount(m.accounts[0]);
  };

  const accountOptions = useMemo(() => {
    const m = PAYMENT_METHODS.find((p) => p.name === method);
    if (!m) return [];
    return accounts
      .filter((a) => m.accounts.includes(a.id))
      .map((a) => ({ label: a.name, value: String(a.id) }));
  }, [method, accounts]);

  // ---------- Allocation totals + validation ----------
  const amountNum = parseFloat(amount) || 0;
  const totalAllocated = useMemo(
    () =>
      Object.values(allocations).reduce(
        (s, v) => s + (parseFloat(v) || 0),
        0
      ),
    [allocations]
  );
  const overAllocated = totalAllocated - amountNum > 0.005;
  const unallocated = Math.max(0, amountNum - totalAllocated);
  const overRows = useMemo(() => {
    const set = new Set();
    for (const inv of invoices) {
      const v = parseFloat(allocations[inv.id]) || 0;
      if (v > inv.due + 0.005) set.add(inv.id);
    }
    return set;
  }, [allocations, invoices]);

  const updateAllocation = (invoiceId, val) =>
    setAllocations((prev) => ({ ...prev, [invoiceId]: val }));

  // Greedy oldest→newest fill of the payment across the open invoices: each one
  // takes the lesser of its balance and what's left. Same rule the
  // PaymentAllocationModal auto-fill uses, but here it's opt-in (this screen
  // defaults every row to $0 on purpose) and it OVERWRITES whatever is typed.
  const autoDistribute = () => {
    let remaining = amountNum;
    const oldestFirst = [...invoices].sort(
      (a, b) => String(a.date || "").localeCompare(String(b.date || "")) || a.id - b.id
    );
    const next = {};
    for (const inv of oldestFirst) {
      const take = Math.min(Math.max(0, remaining), inv.due);
      next[inv.id] = take > 0.005 ? Math.round(take * 100) / 100 : 0;
      remaining -= next[inv.id];
    }
    setAllocations(next);
  };

  // ---------- Submit ----------
  const submit = async () => {
    if (!client || amountNum <= 0) return;
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      // Defense against double-entry. Mirrors AddPayment.
      const recentCutoff = new Date(Date.now() - 2 * 60 * 1000).toISOString();
      const { data: existing } = await supabase
        .from("transactions")
        .select("id, amount, created_at")
        .eq("type", "income")
        .eq("isDeposit", false)
        .eq("amount", amountNum)
        .eq("account", parseInt(account))
        .eq("billing_entity_type", client.client_type)
        .eq("billing_entity_id", client.id)
        .gte("created_at", recentCutoff)
        .limit(1);
      if (existing && existing.length > 0) {
        const e = existing[0];
        const ago = Math.max(1, Math.round((Date.now() - new Date(e.created_at).getTime()) / 1000));
        const ok = window.confirm(
          `Ya se registró un pago de $${amountNum} hace ${ago}s para este cliente. ¿Registrar otro de todos modos?`
        );
        if (!ok) return;
      }

      // Derive card_type from method (mirrors AddPayment).
      let cardType = null;
      if (method === "Tarjeta de Crédito") cardType = "credit";
      else if (method === "Tarjeta de Débito") cardType = "debit";

      // 1. Insert transaction header.
      const { data: txn, error: txErr } = await supabase
        .from("transactions")
        .insert({
          type: "income",
          amount: amountNum,
          reservation: null,
          customer: client.client_type === "customer" ? client.id : null,
          isDeposit: false,
          account: parseInt(account),
          card_type: cardType,
          card_last4: cardType ? (cardLast4 || null) : null,
          payment_date: date || null,
          unallocated_amount: amountNum, // applyAllocations will reduce it
          billing_entity_type: client.client_type,
          billing_entity_id: client.id,
          invoiced: false,
          created_by: session?.user?.id,
        })
        .select("id")
        .single();
      if (txErr) throw txErr;

      // 2. Build allocations array (only invoices with > 0).
      const list = invoices
        .map((inv) => ({
          invoice: inv,
          amount: Math.round((parseFloat(allocations[inv.id]) || 0) * 100) / 100,
        }))
        .filter((a) => a.amount > 0);

      // 3. Apply: writes invoice_payment_allocations, updates each invoice's
      //    amount_paid/status, distributes back to reservations, sets the
      //    transaction's residual unallocated_amount + invoiced flag.
      const { unallocated: residual } = await applyAllocations({
        transactionId: txn.id,
        transactionAmount: amountNum,
        allocations: list,
        createdBy: session?.user?.id,
      });

      setSuccess({
        transactionId: txn.id,
        applied: amountNum - residual,
        residual,
      });
    } catch (err) {
      console.error(err);
      setError(err.message || "Error al registrar pago");
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  };

  const isCard = method === "Tarjeta de Crédito" || method === "Tarjeta de Débito";
  const canSubmit =
    !!client &&
    amountNum > 0 &&
    totalAllocated > 0 &&
    !overAllocated &&
    overRows.size === 0 &&
    !saving &&
    (!isCard || /^\d{4}$/.test(cardLast4));

  // ---------- Render ----------
  return (
    <AppWrapper>
      <Page
        title="Registrar Pago"
        backAction={{ url: "/dashboard" }}
        primaryAction={
          !success
            ? {
                content: saving ? "Registrando..." : "Registrar Pago",
                onAction: submit,
                loading: saving,
                disabled: !canSubmit,
              }
            : {
                content: "Registrar otro",
                onAction: () => window.location.reload(),
              }
        }
      >
        <BlockStack gap="400">
          {error && (
            <Banner tone="critical">
              <p>{error}</p>
            </Banner>
          )}
          {success && (
            <Banner tone="success">
              <p>
                Pago registrado: {formatPrice(success.applied)} aplicado a
                facturas
                {success.residual > 0 && (
                  <>
                    , {formatPrice(success.residual)} pendiente conciliación
                  </>
                )}
                .
              </p>
            </Banner>
          )}

          {/* --- Client --- */}
          <Card>
            <BlockStack gap="300">
              <Text variant="headingSm">Cliente</Text>
              {client ? (
                <InlineGrid columns="1fr auto" alignItems="center">
                  <BlockStack gap="050">
                    <Text variant="bodyMd" fontWeight="semibold">
                      {client.name}
                    </Text>
                    <Text variant="bodySm" tone="subdued">
                      {client.detail}
                    </Text>
                  </BlockStack>
                  <Button
                    variant="plain"
                    onClick={() => setClient(null)}
                    disabled={saving || !!success}
                  >
                    Cambiar
                  </Button>
                </InlineGrid>
              ) : (
                <Button onClick={() => setClientSearchOpen(true)}>
                  Seleccionar cliente
                </Button>
              )}
            </BlockStack>
          </Card>

          {/* --- Payment header --- */}
          {client && (
            <Card>
              <BlockStack gap="300">
                <Text variant="headingSm">Datos del pago</Text>
                <InlineGrid columns={2} gap="200">
                  <TextField
                    label="Monto"
                    type="number"
                    prefix="$"
                    value={amount}
                    onChange={setAmount}
                    autoComplete="off"
                    placeholder="0.00"
                  />
                  <TextField
                    label="Fecha"
                    type="date"
                    value={date}
                    onChange={setDate}
                    autoComplete="off"
                  />
                </InlineGrid>
                <InlineGrid columns={2} gap="200">
                  <Select
                    label="Forma de pago"
                    options={PAYMENT_METHODS.map((p) => ({
                      label: p.name,
                      value: p.name,
                    }))}
                    value={method}
                    onChange={handleMethodChange}
                  />
                  <Select
                    label="Cuenta"
                    options={accountOptions}
                    value={String(account)}
                    onChange={(v) => setAccount(parseInt(v))}
                    disabled={accountOptions.length === 0}
                  />
                </InlineGrid>
                {isCard && (
                  <TextField
                    label="Últimos 4 dígitos de la tarjeta"
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    value={cardLast4}
                    onChange={(v) => setCardLast4(v.replace(/\D/g, "").slice(0, 4))}
                    error={cardLast4 && !/^\d{4}$/.test(cardLast4) ? "4 dígitos" : undefined}
                    helpText="Requerido para conciliación de tarjeta"
                    autoComplete="off"
                  />
                )}
              </BlockStack>
            </Card>
          )}

          {/* --- Open invoices --- */}
          {client && (
            <Card>
              <BlockStack gap="300">
                <InlineGrid columns="1fr auto" alignItems="center">
                  <Text variant="headingSm">Facturas a aplicar</Text>
                  {invoices.length > 0 && (
                    <Button
                      variant="plain"
                      onClick={autoDistribute}
                      disabled={amountNum <= 0}
                    >
                      Auto-distribuir
                    </Button>
                  )}
                </InlineGrid>
                {loadingInvoices ? (
                  <Spinner size="small" />
                ) : invoices.length === 0 ? (
                  <Text tone="subdued">
                    Este cliente no tiene facturas con saldo pendiente. El
                    pago quedará como pendiente conciliación.
                  </Text>
                ) : (
                  <>
                    <IndexTable
                      resourceName={{ singular: "factura", plural: "facturas" }}
                      selectable={false}
                      itemCount={invoices.length}
                      headings={[
                        { title: "Folio" },
                        { title: "Fecha" },
                        { title: "Reservación" },
                        { title: "Total" },
                        { title: "Saldo" },
                        { title: "Aplicar" },
                      ]}
                    >
                      {invoices.map((inv, i) => {
                        const resIds = Object.keys(
                          inv.reservation_attribution || {}
                        );
                        return (
                          <IndexTable.Row
                            id={String(inv.id)}
                            key={inv.id}
                            position={i}
                          >
                            <IndexTable.Cell>
                              <Text variant="bodySm" fontWeight="semibold">
                                {inv.invoice_number || `#${inv.alegra_id}`}
                              </Text>
                            </IndexTable.Cell>
                            <IndexTable.Cell>
                              <Text variant="bodySm" tone="subdued">
                                {inv.date ? formatDate(inv.date) : "—"}
                              </Text>
                            </IndexTable.Cell>
                            <IndexTable.Cell>
                              {/* A single CFDI can cover a dozen reservations;
                                  left unbounded the list stretches the column
                                  and squeezes the Aplicar field. Fixed width +
                                  ellipsis, full list on hover. */}
                              <div
                                style={{
                                  width: "9rem",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={
                                  resIds.length > 0
                                    ? resIds.map((r) => `#${r}`).join(", ")
                                    : undefined
                                }
                              >
                                <Text variant="bodySm" tone="subdued">
                                  {resIds.length > 0
                                    ? resIds.map((r) => `#${r}`).join(", ")
                                    : "—"}
                                </Text>
                              </div>
                            </IndexTable.Cell>
                            <IndexTable.Cell>
                              <Text variant="bodySm">
                                {formatPrice(inv.total)}
                              </Text>
                            </IndexTable.Cell>
                            <IndexTable.Cell>
                              <Text variant="bodySm" tone="critical">
                                {formatPrice(inv.due)}
                              </Text>
                            </IndexTable.Cell>
                            <IndexTable.Cell>
                              {/* Wide enough for an 8-digit amount (10,000,000)
                                  plus the $ prefix and the number spinner. */}
                              <div style={{ minWidth: "11rem" }}>
                                <TextField
                                  label=""
                                  labelHidden
                                  type="number"
                                  prefix="$"
                                  value={String(allocations[inv.id] ?? 0)}
                                  onChange={(v) => updateAllocation(inv.id, v)}
                                  autoComplete="off"
                                  error={
                                    overRows.has(inv.id)
                                      ? "Excede el saldo"
                                      : undefined
                                  }
                                />
                              </div>
                            </IndexTable.Cell>
                          </IndexTable.Row>
                        );
                      })}
                    </IndexTable>

                    <BlockStack gap="100">
                      <InlineGrid columns="1fr auto">
                        <Text>Aplicado</Text>
                        <Text>{formatPrice(totalAllocated)}</Text>
                      </InlineGrid>
                      <InlineGrid columns="1fr auto">
                        <Text>Monto del pago</Text>
                        <Text>{formatPrice(amountNum)}</Text>
                      </InlineGrid>
                      <InlineGrid columns="1fr auto">
                        <Text fontWeight="bold">
                          {overAllocated
                            ? "Sobreasignado"
                            : "Pendiente conciliación"}
                        </Text>
                        <Text
                          fontWeight="bold"
                          tone={
                            overAllocated
                              ? "critical"
                              : unallocated > 0.01
                              ? "attention"
                              : "success"
                          }
                        >
                          {overAllocated
                            ? `-${formatPrice(totalAllocated - amountNum)}`
                            : formatPrice(unallocated)}
                        </Text>
                      </InlineGrid>
                      {overAllocated && (
                        <Text variant="bodySm" tone="critical">
                          La suma de las aplicaciones excede el monto del pago.
                        </Text>
                      )}
                      {!overAllocated && unallocated > 0.01 && (
                        <Text variant="bodySm" tone="subdued">
                          El remanente quedará como crédito del cliente para
                          aplicar a futuras facturas.
                        </Text>
                      )}
                    </BlockStack>
                  </>
                )}
              </BlockStack>
            </Card>
          )}
        </BlockStack>

        <ClientSearch
          open={clientSearchOpen}
          onClose={() => setClientSearchOpen(false)}
          onSelect={(r) => {
            setClient(r);
            setClientSearchOpen(false);
          }}
        />
      </Page>
    </AppWrapper>
  );
}

export default CreatePayment;
